import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { MAX_LABEL_LENGTH, patientFacingDefault, REQUIREMENT_KIND_LABELS, type RequirementKind } from '../constants';
import type { DefinitionCreateRequest, DefinitionResponse, SubjectLanguage } from '../types';

import { MaterializationSummary } from './MaterializationSummary';

export interface CreateRequirementDialogProps {
  open: boolean;
  /** Kinds the org may still add — a taken singleton kind is absent (AC-1). */
  availableKinds: RequirementKind[];
  onClose: () => void;
  onCreate: (body: DefinitionCreateRequest) => Promise<DefinitionResponse>;
  /** Live definition while its panel evaluation runs, or undefined before create. */
  materializing?: DefinitionResponse;
  isChecking?: boolean;
  subject: SubjectLanguage;
}

/**
 * Create a requirement definition, then hold the dialog open on the activation
 * confirmation (AC-21): creating a definition materializes a row for every
 * active patient, and staff see the counts without re-triggering anything.
 */
export const CreateRequirementDialog = ({
  open,
  availableKinds,
  onClose,
  onCreate,
  materializing,
  isChecking = false,
  subject,
}: CreateRequirementDialogProps) => {
  const [kind, setKind] = useState<RequirementKind | ''>('');
  const [label, setLabel] = useState('');
  /**
   * Whether the admin has touched the name field.
   *
   * The prefill re-applies on a type change only while this is false. Once
   * they have written something, changing the type must not overwrite it —
   * losing typed words to a dropdown is the kind of thing people do not
   * forgive, and the type change is usually the correction, not the intent.
   */
  const [labelTouched, setLabelTouched] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [defaultForNew, setDefaultForNew] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);

  const reset = () => {
    setKind('');
    setLabel('');
    setLabelTouched(false);
    setInstructions('');
    setDefaultForNew(true);
    setSubmitting(false);
    setError(null);
    setCreated(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!kind) return;
    // Cleared to blank: fall back to the type's default rather than send an
    // empty label the contract refuses.
    const submittedLabel = label.trim() || patientFacingDefault(kind);
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({
        kind,
        label: submittedLabel,
        patient_instructions: instructions.trim() || null,
        default_for_new_patients: defaultForNew,
      });
      setCreated(true);
    } catch (caught) {
      // 409 is the org already holding this kind — a second admin can win the
      // race between this dialog opening and submitting, so say what happened
      // rather than showing a bare failure.
      const status = (caught as { status?: number })?.status;
      setError(
        status === 409
          ? 'This organization already has a requirement of that kind. Close and reopen to see the current catalog.'
          : 'Could not create the requirement. Please try again.',
      );
      setSubmitting(false);
    }
  };

  /**
   * The name never blocks submission (GDN-3365). It always carries the type's
   * default, so an empty one means the admin deliberately cleared it — and the
   * server requires a label, so `handleSubmit` falls back rather than sending
   * a blank or refusing to act on a button that looked available.
   */
  const canSubmit = Boolean(kind) && !submitting;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{created ? 'Requirement added' : 'Add a requirement'}</DialogTitle>

      <DialogContent>
        {created ? (
          <MaterializationSummary
            definition={materializing}
            isChecking={isChecking}
            subject={subject}
          />
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {availableKinds.length === 0 ? (
              <Alert severity="info">
                Every requirement type this practice can add is already in the catalog.
              </Alert>
            ) : (
              <TextField
                select
                label="Requirement type"
                value={kind}
                onChange={event => {
                  const next = event.target.value as RequirementKind;
                  setKind(next);
                  /*
                    Prefill only while untouched — see `labelTouched`.

                    No form title is passed because this dialog has no form
                    picker: `form` requirements are Slice C, so there is no
                    selected form to take a title from. `patientFacingDefault`
                    accepts one for when there is; until then `form` prefills
                    the type-level fallback. Raised with the PM.
                  */
                  if (!labelTouched) setLabel(patientFacingDefault(next));
                }}
                fullWidth
                size="small"
              >
                {availableKinds.map(available => (
                  <MenuItem key={available} value={available}>
                    {REQUIREMENT_KIND_LABELS[available]}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/*
              Prefilled and editable, never a gate (GDN-3365, Marcus's pick).
              The old field was required, empty, and blocked the primary button
              — so the first thing a practice admin met was a validation error
              on a field whose purpose the dialog never explained. The type
              already has a name; this one says what the PATIENT is told.
            */}
            <TextField
              label={`Name ${subject.plural} will see`}
              value={label}
              onChange={event => {
                setLabel(event.target.value);
                setLabelTouched(true);
              }}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { maxLength: MAX_LABEL_LENGTH } }}
              helperText={`Shown in texts, emails, and the ${subject.singular}'s checklist. Edit it if your practice uses different wording.`}
            />

            <TextField
              label={`${subject.singularCap} instructions (optional)`}
              value={instructions}
              onChange={event => setInstructions(event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={defaultForNew}
                  onChange={event => setDefaultForNew(event.target.checked)}
                />
              }
              label={`Assign to new ${subject.plural} automatically`}
            />

            <Typography variant="caption" color="text.secondary">
              Adding a requirement checks every active {subject.singular} and records who
              already meets it. No messages are sent.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {created ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              variant="contained"
              disabled={!canSubmit}
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Add requirement
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
